import { useId, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Download, ShieldCheck, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  bulkToCsv,
  convertAccount,
  convertBulk,
  type ConvertError,
  type ConvertResult,
} from "@/lib/banking/accountConvert";
import { formatIban } from "@/lib/banking/czechAccount";
import { localePath, makeTr, type LandingLocale } from "@/lib/landing/locale";
import { TOOL_MAX_BULK_LINES, TOOL_MAX_LINE_LENGTH } from "@/lib/tools/limits";
import { track } from "@/lib/tracking/analytics";
import { cn } from "@/lib/utils";

/**
 * The free account ↔ IBAN converter: one account or IBAN with a live answer,
 * or many pasted one per line with a table and a CSV download. Everything
 * runs in this tab; after consent one `tool_used` event per CSV download,
 * with nothing that was typed.
 */

const PREVIEW_ROWS = 50;
/** Bulk text box cap: lines × line length, with room for line breaks. */
const MAX_BULK_CHARS = TOOL_MAX_BULK_LINES * (TOOL_MAX_LINE_LENGTH + 2);

const ERRORS: Record<ConvertError, [string, string]> = {
  too_long: ["Příliš dlouhý řádek.", "Line too long."],
  unrecognised: [
    "Tohle není číslo účtu ani IBAN. Číslo účtu pište s kódem banky, např. 19-2000145399/0800.",
    "This is neither an account number nor an IBAN. Write the account with its bank code, e.g. 19-2000145399/0800.",
  ],
  account_check: [
    "Číslo účtu neprošlo kontrolou (modulo 11). Zkontrolujte číslice.",
    "The account number fails its check (modulo 11). Check the digits.",
  ],
  iban_check: [
    "IBAN neprošel kontrolou (modulo 97). Zkontrolujte číslice.",
    "The IBAN fails its check (modulo 97). Check the digits.",
  ],
  iban_foreign: [
    "Zahraniční IBAN: kontrolní číslice ověříme, na české číslo účtu ho ale převést nejde.",
    "A foreign IBAN: its check digits are checked, but it has no Czech account number.",
  ],
  unknown_bank: [
    "Kód banky není v číselníku ČNB.",
    "The bank code is not on the Czech National Bank's list.",
  ],
};

function ResultCard({ r, locale }: { r: ConvertResult; locale: LandingLocale }) {
  const tr = makeTr(locale);
  const rows: [string, string][] = [
    [tr("Číslo účtu", "Account number"), r.account],
    ["IBAN", r.iban ? formatIban(r.iban) : ""],
    [tr("Banka", "Bank"), r.bankName ? `${r.bankName} (${r.bankCode})` : r.bankCode],
    ["BIC (SWIFT)", r.bic],
  ].filter(([, v]) => v) as [string, string][];
  return (
    <div className="mt-4 rounded-lg border bg-background p-4">
      <p
        className={cn(
          "flex items-start gap-2 text-sm font-medium",
          r.valid ? "text-[hsl(var(--action))] dark:text-primary" : "text-destructive"
        )}
      >
        {r.valid ? (
          <CheckCircle2 aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
        ) : (
          <XCircle aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
        )}
        {r.valid && !r.error
          ? tr("Platné: kontrolní číslice sedí.", "Valid: the check digits hold.")
          : r.error
            ? tr(...(ERRORS[r.error] as [string, string]))
            : tr("Neplatné.", "Not valid.")}
      </p>
      {rows.length > 0 && (
        <dl className="mt-3 grid grid-cols-1 gap-x-4 text-sm sm:grid-cols-[auto_1fr] sm:gap-y-1">
          {rows.map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="text-xs text-muted-foreground sm:text-sm">{label}</dt>
              <dd className="mb-2 break-all font-mono tabular-nums sm:mb-0">{value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

export function AccountIbanConverter({ locale }: { locale: LandingLocale }) {
  const tr = makeTr(locale);
  const id = useId();
  const [single, setSingle] = useState("");
  const [bulk, setBulk] = useState("");
  const [downloaded, setDownloaded] = useState(false);

  const one = useMemo(() => (single.trim() ? convertAccount(single) : null), [single]);
  const many = useMemo(() => (bulk.trim() ? convertBulk(bulk) : null), [bulk]);

  function downloadCsv() {
    if (!many) return;
    const blob = new Blob([bulkToCsv(many.rows, locale)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = tr("ucty-iban.csv", "accounts-iban.csv");
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
    setDownloaded(true);
    track("tool_used", { tool: "account_iban", output: "csv" });
  }

  const validCount = many ? many.rows.filter((r) => r.valid).length : 0;

  return (
    <div className="rounded-xl border bg-card p-5 sm:p-6">
      <Label htmlFor={`${id}-one`}>{tr("Číslo účtu nebo IBAN", "Account number or IBAN")}</Label>
      <Input
        id={`${id}-one`}
        value={single}
        onChange={(e) => setSingle(e.target.value)}
        maxLength={TOOL_MAX_LINE_LENGTH}
        placeholder="19-2000145399/0800"
        autoComplete="off"
        spellCheck={false}
        className="mt-1.5 font-mono"
      />
      <div aria-live="polite">{one && <ResultCard r={one} locale={locale} />}</div>

      <div className="mt-8 border-t pt-6">
        <Label htmlFor={`${id}-bulk`}>
          {tr("Hromadně: jeden účet nebo IBAN na řádek", "In bulk: one account or IBAN per line")}
        </Label>
        <textarea
          id={`${id}-bulk`}
          value={bulk}
          onChange={(e) => setBulk(e.target.value)}
          maxLength={MAX_BULK_CHARS}
          rows={6}
          placeholder={"19-2000145399/0800\nCZ65 0800 0000 1920 0014 5399"}
          spellCheck={false}
          // Not the app's Textarea: it reads the app's mode, which public pages do not have.
          className="mt-1.5 flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        />
        <p className="mt-1 text-xs text-muted-foreground">
          {tr(
            `Nejvýš ${TOOL_MAX_BULK_LINES.toLocaleString("cs-CZ")} řádků.`,
            `At most ${TOOL_MAX_BULK_LINES.toLocaleString("en-GB")} lines.`
          )}
        </p>

        {many && (
          <div className="mt-4" aria-live="polite">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm">
                {tr(
                  `Řádků: ${many.rows.length}, platných: ${validCount}`,
                  `Lines: ${many.rows.length}, valid: ${validCount}`
                )}
                {many.skipped > 0 &&
                  tr(
                    ` · ${many.skipped} řádků nad limit jsme vynechali`,
                    ` · ${many.skipped} lines over the limit were left out`
                  )}
              </p>
              <Button type="button" onClick={downloadCsv}>
                <Download aria-hidden="true" className="mr-2 size-4" />
                {tr("Stáhnout CSV", "Download CSV")}
              </Button>
            </div>
            {/* Phones: one block per line instead of a table that scrolls sideways. */}
            <ul className="mt-3 divide-y rounded-lg border text-sm sm:hidden">
              {many.rows.slice(0, PREVIEW_ROWS).map((r, i) => (
                <li key={i} className="flex items-start gap-2 px-3 py-2.5">
                  {r.valid ? (
                    <CheckCircle2
                      aria-label={tr("platné", "valid")}
                      className="mt-0.5 size-4 shrink-0 text-[hsl(var(--action))] dark:text-primary"
                    />
                  ) : (
                    <XCircle
                      aria-label={tr("neplatné", "not valid")}
                      className="mt-0.5 size-4 shrink-0 text-destructive"
                    />
                  )}
                  <div className="min-w-0">
                    <p className="break-all font-mono">{r.account || r.input}</p>
                    {r.iban && (
                      <p className="break-all font-mono text-xs text-muted-foreground">
                        {formatIban(r.iban)}
                      </p>
                    )}
                    {r.bankName && <p className="text-xs text-muted-foreground">{r.bankName}</p>}
                    {r.error && (
                      <p className="text-xs text-destructive">
                        {tr(...(ERRORS[r.error] as [string, string]))}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-3 hidden overflow-x-auto rounded-lg border sm:block">
              <table className="w-full min-w-[40rem] text-left text-sm">
                <thead className="border-b bg-secondary/40 text-xs text-muted-foreground">
                  <tr>
                    <th scope="col" className="px-3 py-2 font-medium">
                      {tr("Číslo účtu", "Account number")}
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      IBAN
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      {tr("Banka", "Bank")}
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      {tr("Platné", "Valid")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {many.rows.slice(0, PREVIEW_ROWS).map((r, i) => (
                    <tr key={i}>
                      <td className="whitespace-nowrap px-3 py-2 font-mono">
                        {r.account || r.input}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 font-mono">
                        {r.iban ? formatIban(r.iban) : "—"}
                      </td>
                      <td className="px-3 py-2">{r.bankName || r.bankCode || "—"}</td>
                      <td className="px-3 py-2">
                        {r.valid ? (
                          <CheckCircle2
                            aria-label={tr("platné", "valid")}
                            className="size-4 text-[hsl(var(--action))] dark:text-primary"
                          />
                        ) : (
                          <span className="inline-flex items-center gap-1 text-destructive">
                            <XCircle aria-hidden="true" className="size-4 shrink-0" />
                            <span className="text-xs">
                              {r.error ? tr(...(ERRORS[r.error] as [string, string])) : ""}
                            </span>
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {many.rows.length > PREVIEW_ROWS && (
              <p className="mt-2 text-xs text-muted-foreground">
                {tr(
                  `Náhled prvních ${PREVIEW_ROWS} řádků; v CSV jsou všechny.`,
                  `Preview of the first ${PREVIEW_ROWS} lines; the CSV has them all.`
                )}
              </p>
            )}
          </div>
        )}
      </div>

      <p className="mt-6 flex items-start gap-2 text-sm text-muted-foreground">
        <ShieldCheck
          aria-hidden="true"
          className="mt-0.5 size-4 shrink-0 text-[hsl(var(--action))] dark:text-primary"
        />
        {tr(
          "Nic se nikam neodesílá: převod a kontrola proběhnou jen ve vašem prohlížeči.",
          "Nothing is sent anywhere: the conversion and check run only in your browser."
        )}
      </p>

      {downloaded && (
        <p className="mt-5 rounded-lg border bg-secondary/40 px-4 py-3 text-sm leading-relaxed">
          {tr(
            "Chcete platby z Fio stahovat automaticky a párovat je s doklady? ",
            "Want your Fio payments downloaded automatically and matched with your documents? "
          )}
          <Link
            to={localePath(locale, "/")}
            className="inline-flex items-center gap-1 font-medium text-[hsl(var(--action))] underline-offset-4 hover:underline dark:text-primary"
          >
            {tr("Podívejte se na Zettl", "Take a look at Zettl")}
            <ArrowRight aria-hidden="true" className="size-3.5" />
          </Link>
        </p>
      )}
    </div>
  );
}
